import { Button } from '../../interfaces/controller';
import { ButtonSink } from './input-mux';

/**
 * Binds keyboard input to a StandardController.
 *
 * Default mapping (KeyboardEvent.code, layout-independent):
 *   Arrow keys  -> D-pad
 *   KeyX        -> A
 *   KeyZ        -> B
 *   Enter       -> Start
 *   Shift (R/L) -> Select
 *
 * Handled keys are preventDefault()'d so arrows/space don't scroll the page.
 * DOM types are used here on purpose: this module is only imported by the
 * browser entry point (src/index.ts), never by headless tests.
 */
export type KeyMap = { [code: string]: Button };

export const DEFAULT_KEY_MAP: KeyMap = {
    ArrowUp: Button.Up,
    ArrowDown: Button.Down,
    ArrowLeft: Button.Left,
    ArrowRight: Button.Right,
    KeyX: Button.A,
    KeyZ: Button.B,
    Enter: Button.Start,
    ShiftRight: Button.Select,
    ShiftLeft: Button.Select,
};

type EventTargetLike = {
    addEventListener(type: string, listener: (e: any) => void): void;
    removeEventListener(type: string, listener: (e: any) => void): void;
};

export class KeyboardAdapter {
    private sink: ButtonSink;
    private target: EventTargetLike;
    private keyMap: KeyMap;
    private attached = false;

    private onKeyDown = (e: KeyboardEvent) => this.handle(e, true);
    private onKeyUp = (e: KeyboardEvent) => this.handle(e, false);
    private onBlur = () => this.releaseAll();

    constructor(sink: ButtonSink, target: Window | HTMLElement, keyMap: KeyMap = DEFAULT_KEY_MAP) {
        this.sink = sink;
        this.target = target as unknown as EventTargetLike;
        this.keyMap = keyMap;
        this.attach();
    }

    private handle(e: KeyboardEvent, pressed: boolean): void {
        const button = this.keyMap[e.code];
        if (button === undefined) return;
        this.sink.setButton(button, pressed);
        e.preventDefault();
    }

    /** Release every mapped button (e.g. when the window loses focus). */
    releaseAll(): void {
        const buttons = new Set<Button>(Object.values(this.keyMap));
        for (const button of buttons) {
            this.sink.setButton(button, false);
        }
    }

    private attach(): void {
        if (this.attached) return;
        this.target.addEventListener('keydown', this.onKeyDown);
        this.target.addEventListener('keyup', this.onKeyUp);
        // Window-only event; guarded so an HTMLElement target still works.
        if (typeof (this.target as any).addEventListener === 'function') {
            this.target.addEventListener('blur', this.onBlur);
        }
        this.attached = true;
    }

    dispose(): void {
        if (!this.attached) return;
        this.target.removeEventListener('keydown', this.onKeyDown);
        this.target.removeEventListener('keyup', this.onKeyUp);
        this.target.removeEventListener('blur', this.onBlur);
        this.releaseAll();
        this.attached = false;
    }
}
